import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase.js'
import { identifyUser } from '../lib/tracking.js'
import type { OAuthProvider } from '../lib/oauthProviders.js'

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  /** Send a 6-digit OTP code; creates the account on the fly if new. */
  requestCode: (email: string, returnTo?: string) => Promise<void>
  /** Verify the OTP code and establish the session. */
  verifyCode: (email: string, code: string) => Promise<void>
  /** Start a social-login redirect (LinkedIn/Google/Microsoft). */
  signInWithProvider: (provider: OAuthProvider, returnTo?: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** sessionStorage keys that survive the OAuth same-tab round-trip. */
export const OAUTH_RETURN_TO_KEY = 'acp:oauth:returnTo'
export const OAUTH_INVITE_KEY = 'acp:oauth:invite'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      if (event === 'SIGNED_IN' && session?.user) {
        identifyUser(session.user.id, session.user.email ?? '')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Passwordless: one email field serves both new and returning users.
  // `shouldCreateUser: true` registers the account at verify time.
  const requestCode = useCallback(async (email: string, returnTo?: string) => {
    // The magic-link fallback in the email must land on /login — NOT the Site
    // URL root '/'. The session rides back in the URL hash (implicit flow); on
    // '/' the RequireAuth guard bounces through a client redirect that drops
    // the hash before detectSessionInUrl can read it, so the link silently
    // fails (while the 6-digit code, which verifyOtp resolves in place, works).
    // /login is unguarded: detectSessionInUrl parses the hash there and the
    // page's session-watch effect then forwards to returnTo. Mirrors the OAuth
    // redirectTo: `${origin}/login`.
    let emailRedirectTo: string | undefined
    if (typeof window !== 'undefined') {
      const url = new URL('/login', window.location.origin)
      if (returnTo) url.searchParams.set('returnTo', returnTo)
      emailRedirectTo = url.toString()
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true, emailRedirectTo },
    })
    if (error) throw error
  }, [])

  const verifyCode = useCallback(async (email: string, code: string) => {
    const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' })
    if (error) throw error
  }, [])

  const signInWithProvider = useCallback(
    async (provider: OAuthProvider, returnTo?: string) => {
      // The cross-subdomain client runs the IMPLICIT flow (no flowType set, so
      // the shared cookie keeps working). OAuth therefore returns the session
      // in the URL hash and `detectSessionInUrl` establishes it on /login —
      // do NOT add an exchangeCodeForSession (PKCE) callback here.
      // returnTo can't ride the provider round-trip safely, so stash it.
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(OAUTH_RETURN_TO_KEY, returnTo ?? '')
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/login` },
      })
      if (error) throw error
    },
    [],
  )

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, session, loading, requestCode, verifyCode, signInWithProvider, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
